import { useState } from "react";
import { Box, Button, Flex, Icon, Spinner, Text } from "@chakra-ui/react";
import {
  backgroundLight,
  color3,
  greenVivid,
  textPrimary,
  textSecondary,
  white,
  newBorder,
} from "../../../../../utils/colors";
import { useTranslation } from "react-i18next";
import TreatmentPlanHeaderPublic from "../../../../../components/partials/treatmentplans/TreatmentPlanHeaderPublic";
import TreatmentPlanServicesPublic from "../../../../../components/partials/treatmentplans/TreatmentPlanServicesPublic";
import TreatmentPlanPreviewPatientNotes from "../../../../../components/partials/treatmentplans/TreatmentPlanPreviewPatientNotes";
import TreatmentPlanPreviewFooter from "../../../../../components/partials/treatmentplans/TreatmentPlanPreviewFooter";
import { shade } from "polished";
import { FaCircleCheck, FaCircleXmark, FaFilePdf } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ConfirmCancelPublicModal from "../../../../../components/modals/ConfirmCancelPublicModal";
import ConfirmApprovePublicModal from "../../../../../components/modals/ConfirmApprovePublicModal";
import {
  publicApproveTreatmentPlanFromSharedLinkAction,
  publicGetPortalPatientTreatmentPlansAction,
  publicCancelTreatmentPlanFromSharedLinkAction,
  publicDownloadTreatmentPlanPdfFromSharedLinkAction,
} from "../../../../../actions/publicActions"; // ✅ import your new action

const PatientPortalOnePlan = ({
  treatmentPlans,
  clinicName,
  patientPortalPublicDetails,
  token,
}) => {
  const { t, i18n } = useTranslation();

  // ID
  const { slug } = useParams();
  const { tenantID, patientId } = useParams();

  // LANGUAGE

  const currentLanguage = i18n.language;

  // STATE
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showApproveConfirmation, setShowApproveConfirmation] = useState(false);

  const [reason, setReason] = useState("");
  const [reasonComment, setReasonComment] = useState("");

  // REDUX
  const dispatch = useDispatch();

  const publicPortalTreatmentPlanDownloadPdf = useSelector(
    (state) => state.publicPortalTreatmentPlanDownloadPdf
  );

  const {
    loading: loadingDownloadPdf,
    error: errorDownloadPdf,
    success: successDownloadPdf,
  } = publicPortalTreatmentPlanDownloadPdf;

  const publicPortalTreatmentPlanApprove = useSelector(
    (state) => state.publicPortalTreatmentPlanApprove
  );
  const {
    loading: loadingApprove,
    error: errorApprove,
    success: successApprove,
  } = publicPortalTreatmentPlanApprove;

  const publicPortalTreatmentPlanCancel = useSelector(
    (state) => state.publicPortalTreatmentPlanCancel
  );
  const {
    loading: loadingCancel,
    error: errorCancel,
    success: successCancel,
  } = publicPortalTreatmentPlanCancel;

  // HANDLERS
  // Inside component
  const handleDownloadButtonClick = async (planId) => {
    try {
      if (!token) {
        console.error("Missing patient token");
        return;
      }

      const blob = await dispatch(
        publicDownloadTreatmentPlanPdfFromSharedLinkAction(
          slug,
          patientId,
          planId,
          token,
          currentLanguage
        )
      );

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `TreatmentPlan-${planId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed", err);
    }
  };

  const approveHandler = async (id) => {
    try {
      if (!token) {
        console.error("Missing patient token");
        return;
      }
      await dispatch(
        publicApproveTreatmentPlanFromSharedLinkAction(
          slug,
          patientId,
          id,
          token
        )
      );

      // ✅ Refresh treatment plans
      await dispatch(
        publicGetPortalPatientTreatmentPlansAction(slug, patientId, token)
      );

      setShowApproveConfirmation(false);
    } catch (err) {
      console.error("Error approving treatment plan", err);
    }
  };

  const cancelHandler = async (id) => {
    try {
      if (!token) {
        console.error("Missing patient token");
        return;
      }

      // ✅ wait for cancel to complete
      await dispatch(
        publicCancelTreatmentPlanFromSharedLinkAction(
          slug,
          patientId,
          id,
          reason,
          reasonComment,
          token
        )
      );

      // ✅ now refresh the plans
      await dispatch(
        publicGetPortalPatientTreatmentPlansAction(slug, patientId, token)
      );

      setShowCancelConfirmation(false);
    } catch (err) {
      console.error("Error cancelling treatment plan", err);
    }
  };

  // USE EFFECT

  // LOG
  // console.log("treatmentPlans: ", treatmentPlans);
  // console.log("patientPortalPublicDetails: ", patientPortalPublicDetails);

  const plan = treatmentPlans[0];

  return (
    <>
      {/* Render the delete confirmation modal when necessary */}
      {/* <ConfirmApprovePublicModal
          isOpen={isConfirmModalOpen}
          onClose={closeModal}
          confirmAction={confirmApprove}
          treatmentPlanDetails={selectedTreatmentPlan}
        /> */}

      {showCancelConfirmation && (
        <ConfirmCancelPublicModal
          isOpen={showCancelConfirmation}
          onClose={() => {
            setShowCancelConfirmation(false);
            setReason("");
            setReasonComment("");
          }}
          confirmAction={() => cancelHandler(plan._id)}
          treatmentPlanDetails={plan}
          setReason={setReason}
          setReasonComment={setReasonComment}
          reason={reason}
          reasonComment={reasonComment}
        />
      )}

      {/* Render the delete confirmation modal when necessary */}
      {showApproveConfirmation && (
        <ConfirmApprovePublicModal
          isOpen={showApproveConfirmation}
          onClose={() => setShowApproveConfirmation(false)}
          confirmAction={() => {
            approveHandler(plan._id);
            setShowApproveConfirmation(false);
          }}
          treatmentPlanDetails={plan}
        />
      )}

      <Flex
        w="100%"
        align="center"
        justify="center"
        direction="column"
        maxW={["100%", "800px"]}
        m="0 auto"
        p="2rem 0 0"
      >
        <Box w="100%">
          <Flex
            w="100%"
            direction="column"
            bg={backgroundLight}
            borderRadius={["0rem", "2rem"]}
            p={["1rem 1rem", "2rem"]}
            h="100%"
            overflowY="auto"
            className="customScrollbar"
          >
            {/* EMAIL PREVIEW */}
            <Flex
              direction="column"
              bg={white}
              p={["1.5rem 0", "1.5rem"]}
              borderRadius="1.25rem"
              boxShadow="0px 4px 8px rgba(0, 0, 0, 0.05)"
            >
              {/* CLINIC HEADER */}
              <Box>
                {/* <TreatmentPlanClinicHeaderPublic tenant={tenant} /> */}
              </Box>
              {/* TREATMENT PLAN HEADER */}
              <Box>
                <TreatmentPlanHeaderPublic treatmentPlan={plan} />
              </Box>
              {/* TREATMENT PLAN SERVICES */}
              <Box>
                <Flex w="100%" p="1rem 0 1rem">
                  <TreatmentPlanServicesPublic
                    treatmentPlan={plan}
                    currency={patientPortalPublicDetails.currency}
                  />
                </Flex>
              </Box>
              {/* TREATMENT PLAN PATIENT NOTES */}
              <Box>
                <TreatmentPlanPreviewPatientNotes notes={plan.notes} />
              </Box>

              {/* TREATMENT PLAN FOOTER */}
              <Box>
                <TreatmentPlanPreviewFooter
                  tenant={patientPortalPublicDetails}
                />
              </Box>
            </Flex>
          </Flex>
        </Box>

        {/* ACTIONS */}
        <Flex
          align="center"
          justify="center"
          w="100%"
          m="4rem 0 0"
          direction="column"
          p={["0 1.5rem", "0 3rem"]}
        >
          {/*  ACTIONS TITLE */}
          {plan.status === "planning" && (
            <>
              <Text
                fontSize="1.25rem"
                fontWeight="500"
                color={textPrimary}
                textAlign="center"
              >
                {t("treatmentPlan.publicApproveText")}
              </Text>
              <Text
                m="0.5rem 0 0"
                fontSize="md"
                fontWeight="500"
                color={textSecondary}
                textAlign="center"
              >
                {t("treatmentPlan.publicApproveTextShort")}
              </Text>
            </>
          )}

          {plan.status === "approved" && (
            <>
              <Text
                fontSize="1.25rem"
                fontWeight="500"
                color={textPrimary}
                textAlign="center"
              >
                {t("treatmentPlan.publicApproveTextApproved")}
              </Text>
              <Text
                m="0.5rem 0 0"
                fontSize="md"
                fontWeight="500"
                color={textSecondary}
                textAlign="center"
              >
                {t("treatmentPlan.publicApproveTextShortApproved")}
              </Text>
            </>
          )}

          {plan.status === "ongoing" && (
            <>
              <Text
                fontSize="1.25rem"
                fontWeight="500"
                color={textPrimary}
                textAlign="center"
              >
                {t("treatmentPlan.publicApproveTextOngoing")}
              </Text>
              <Text
                m="0.5rem 0 0"
                fontSize="md"
                fontWeight="500"
                color={textSecondary}
                textAlign="center"
              >
                {t("treatmentPlan.publicApproveTextShortOngoing")}
              </Text>
            </>
          )}
          {plan.status === "completed" && (
            <>
              <Text
                fontSize="1.25rem"
                fontWeight="500"
                color={textPrimary}
                textAlign="center"
              >
                {t("treatmentPlan.publicApproveTextCompleted")}
              </Text>
              <Text
                m="0.5rem 0 0"
                fontSize="md"
                fontWeight="500"
                color={textSecondary}
                textAlign="center"
              >
                {t("treatmentPlan.publicApproveTextShortCompleted")}
              </Text>
            </>
          )}

          {plan.status === "cancelled" && (
            <>
              <Text
                fontSize="1.25rem"
                fontWeight="500"
                color={textPrimary}
                textAlign="center"
              >
                {t("treatmentPlan.publicApproveTextCancelled")}
              </Text>
              <Text
                m="0.5rem 0 0"
                fontSize="md"
                fontWeight="500"
                color={textSecondary}
                textAlign="center"
              >
                {t("treatmentPlan.publicApproveTextShortCancelled")}
              </Text>
            </>
          )}

          {/*  ACTIONS BUTTONS */}
          <Flex
            w="100%"
            align="center"
            justify="center"
            p="3rem 0"
            gap="1rem"
            direction={["column", "row"]}
          >
            <Button
              borderRadius="10rem"
              bg={backgroundLight}
              color={textSecondary}
              fontSize="sm"
              size="lg"
              w={["100%", "auto"]}
              fontWeight="500"
              onClick={() => setShowCancelConfirmation(true)}
              isDisabled={
                plan.status === "cancelled" ||
                plan.status === "ongoing" ||
                plan.status === "completed"
              }
            >
              <Icon as={FaCircleXmark} mr={2} fontSize="0.75rem" />
              {t("treatmentPlan.cancel")}
            </Button>

            <Button
              borderRadius="10rem"
              bg={color3}
              color={white}
              fontSize="sm"
              size="lg"
              w={["100%", "auto"]}
              fontWeight="500"
              _hover={{
                bg: shade(0.1, color3),
              }}
              onClick={() => setShowApproveConfirmation(true)}
              isDisabled={
                plan.status === "approved" ||
                plan.status === "ongoing" ||
                plan.status === "completed"
                // plan.status === "cancelled"
              }
            >
              <Icon as={FaCircleCheck} mr={2} fontSize="0.75rem" />
              {t("treatmentPlan.approve")}
            </Button>

            <Button
              bg={white}
              border={`1px solid ${newBorder}`}
              color={textSecondary}
              size="lg"
              w={["100%", "auto"]}
              borderRadius="10rem"
              _hover={{
                bg: greenVivid,
                color: textPrimary,
              }}
              onClick={() => handleDownloadButtonClick(plan._id)}
            >
              {loadingDownloadPdf ? (
                <Flex align="center">
                  <Spinner
                    _groupHover={{
                      color: white,
                    }}
                    size="sm"
                    mr={2}
                  />
                  <Text fontSize="sm" fontWeight="500">
                    {t("common.downloading")}
                  </Text>
                </Flex>
              ) : (
                <Icon as={FaFilePdf} fontSize="0.875rem" />
              )}
            </Button>
          </Flex>

          {/* BOTTOM */}
          <Flex
            justify="center"
            align="center"
            w="100%"
            borderTop={`1px solid ${newBorder}`}
            m="6rem 0 0"
            p="1.5rem 0 0rem"
            direction="column"
          >
            <Text
              fontSize="sm"
              color={textSecondary}
              fontWeight="500"
              textAlign="center"
              maxW="500px"
              m="0 auto"
              lineHeight="1.5"
            >
              {t("treatmentPlan.portalBottomText")}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default PatientPortalOnePlan;
